import React, {ReactNode, useEffect, useState} from "react";
import {ResultStatusType} from "../../domain/verification/ResultStatusType";
import './ResultComponent.scss'
import {ButtonComponent} from "../common/button/ButtonComponent";
import {isMobileDevice} from "../../utils/WindowUtils";
import {ThemedLottiePlayerComponent} from "../common/lottie-player/ThemedLottiePlayerComponent";
import {useThemeSelector} from "../../hooks/theme/useThemeSelector";

interface ResultComponentProps {
    result: ResultStatusType
    name: string
    failureMessage: string | null
}

export const ResultComponent: React.FC<ResultComponentProps> = ({ result, name, failureMessage }: ResultComponentProps) => {

    const { theme } = useThemeSelector()
    const [uppercase, setUppercase] = useState(false)
    const [supportLink, setSupportLink] = useState("")

    useEffect(() => {
        if (theme === "squareone") {
            setUppercase(true)
            setSupportLink("https://getsquareone.app/")
        } else if (theme === "emerge") {
            setSupportLink("https://www.emerge.nz/")
        }
    }, [theme]);

    const showResult = () => {
        switch (result) {
            case ResultStatusType.PENDING:
                return showPending()
            case ResultStatusType.REVIEW:
                const title = "Your ID is being checked"
                return createResultComponent(
                    "review",
                    (<div className={`review-title`}>{uppercase ? title.toUpperCase() : title}</div>),
                    `Thanks ${capitalizeName(name)}, we've got all that info and we're on it. 
                    There's no need to wait here, you can close the app and we'll message you when we're done.`,
                    true,
                    () => { window.location.href = supportLink })
            case ResultStatusType.FAIL:
                if (failureMessage === 'below_age_limit') {
                    return createResultComponent(
                        "fail",
                        (<div className={`text-align-center`}>A little too early to Emerge!</div>),
                        (<div className={`flex-center`}>
                            <div>{`Sorry ${capitalizeName(name)}, you're not quite old enough to use Emerge - you need to be 13 or older.`}</div>
                            <div>In the meantime, we recommend checking out <strong>SquareOne</strong> with your family.</div>
                            <div className={`flex-center`}>It's a great way to get started!</div>
                        </div>),
                        true,
                        () => {
                            window.location.href = "https://getsquareone.app/"
                        },
                        'Check out SquareOne'
                    )
                } else {
                    return createResultComponent(
                        "fail",
                        (<span>{uppercaseTextIfNeeded('Let us ')}<span
                            className="title-highlight">{uppercaseTextIfNeeded('help')}</span></span>),
                        `Sorry ${capitalizeName(name)}, we couldn’t verify your identity. Please contact us below so we can get things sorted for you.`,
                        true,
                        () => {
                            window.location.href = supportLink
                        },
                        "Talk to us")
                }
            case ResultStatusType.ERROR:
                return createResultComponent(
                    "fail",
                    (<span>{uppercaseTextIfNeeded('Let us ')}<span
                        className="title-highlight">{uppercaseTextIfNeeded('help')}</span></span>),
                    `Sorry ${capitalizeName(name)}, we couldn’t verify your identity. Please contact us below so we can get things sorted for you.`,
                    true,
                    () => {
                        window.location.href = supportLink
                    })
            case ResultStatusType.PASS:
                return createResultComponent(
                    "success",
                    (<span>{uppercaseTextIfNeeded('Thanks, ')}<span className="title-highlight">{capitalizeName(name, true)}</span></span>),
                    `Your ID verification check is complete`)
            default:
                return 'Error'
        }
    }

    const showPending = () => (
        <>
            <div className={`flex-center result-lottie-container`}>
                <ThemedLottiePlayerComponent animation="pending"/>
            </div>
            <span className={`font-xs result-pending-text width-full flex-center`}>Checking info...</span>
        </>
    )

    const createResultComponent = (
        animation: string,
        title: ReactNode,
        text: ReactNode,
        isButtonPresent?: boolean,
        buttonAction?: () => void,
        buttonLabel?: string,
    ) => {
        return (
            <>
                <div className={`flex-center result-lottie-container`}>
                    <ThemedLottiePlayerComponent animation={animation}/>
                </div>
                <div className={`flex-center result-text-container`}>
                <span className={`font-white general-title general-result-title result-title-font`}>
                    {title}
                </span>
                    <span className={`flex-center font-white general-subtitle result-title-text`}>
                    {text}
                </span>
                </div>
                { isButtonPresent && buttonAction ? (
                    <div className={'flex-center width-full result-button-container'}>
                        <ButtonComponent label={buttonLabel ? buttonLabel : 'Contact support'} onButtonClick={buttonAction}/>
                    </div>
                ) : ('')}
            </>
        )
    }

    const capitalizeName = (name: string, title?: boolean): string => {
        if (uppercase && title) {
            return name.toUpperCase()
        }
        let capitalizedName = name.toLowerCase()
        const capitalizedFirstLetter = capitalizedName[0].toUpperCase()
        capitalizedName = capitalizedName.substring(1)
        return capitalizedFirstLetter + capitalizedName
    }

    const uppercaseTextIfNeeded = (text: string) => {
        if (uppercase) {
            return text.toUpperCase()
        }
        return text
    }

    return (
        <div className={`background-complete general-container result-container height-window 
        ${isMobileDevice() ? 'tertiary-blue-background' : ''}`}>
            {showResult()}
        </div>
    );
}